import React, { useRef } from "react";

import { css } from "@emotion/react";

import { ComponentRenderer } from "../../_layout-helpers/component-renderer";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { HeaderSpacer } from "../../_layout-helpers/spacers";
import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { StoryLayoutTags } from "../story-page/story-layout-tags";
import { StoryLayoutHeadlineLarge } from "../story-page/story-layout-headline-large";
import { useNavigate, useLocation } from "react-router";
import { SwarovskiOptikVideoPlayer } from "../../../components/VideoComponent/VideoComponent";
import { useScrollToElementOnIdle } from "../../../hooks";
import { PageMetadataComponent } from "../../../components/PageMetadataComponent/PageMetadataComponent";
import { StoryLayoutReadingTime } from "../story-page/story-layout-reading-time";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { ExtractSwarovskiPageData, VideoStoryPageData } from "@swo/storefront";
import { useStore } from "../../../store/hooks/use-store";
import { VideoPlayerWithUi } from "../../../components/VideoComponent/VideoPlayerWithUi";
import { t2 } from "../../../domain/translations/translations";

export const VideoStoryLayout: React.FC<ExtractSwarovskiPageData<VideoStoryPageData>> = props => {
  const location = useLocation();
  const navigate = useNavigate();
  const { locationOrigin } = useStore().state;
  const gt = t2(props.generalTranslations);

  const shouldScrollToVideoOnMount = location.hash === "#v";

  const ref = useRef<HTMLDivElement | null>(null);
  useScrollToElementOnIdle(ref, shouldScrollToVideoOnMount, () => {
    return navigate(
      {
        hash: "",
      },
      {
        replace: true,
      },
    );
  });

  return (
    <React.Fragment>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <Spacer size={"md"} />
      <ContentContainer
        attachStyles={css`
          display: flex;
          flex-wrap: wrap;
        `}
      >
        {typeof props.data.videoLength !== "undefined" && (
          <StoryLayoutReadingTime readingTime={props.data.videoLength} />
        )}
      </ContentContainer>
      <Spacer size={"md"} />

      <StoryLayoutHeadlineLarge
        emoHeadline={props.data.emotionalHeadline}
        seoHeadline={props.data.seoHeadline}
        alignment={props.data.alignHeadlines}
      />
      <Spacer size={"lg"} />

      <div ref={ref}>
        <VideoPlayerWithUi {...props.data.video} resolutionPickerStrategy={"middle"} mode={"fill"} />
      </div>

      <Spacer size={"lg"} />

      <ComponentRenderer components={props.data.components} localeForNumberFormat={props.locale} translations={gt} />

      <Spacer size={"md"} />
      <FooterComponent {...props.data.footer} />
    </React.Fragment>
  );
};
